/**
 * Google Analytics 4 — prod-only page_view tracking (quick-260727-n07).
 *
 * Gate: `import.meta.env.PROD && VITE_GA_MEASUREMENT_ID` (mirrors the Sentry gate
 * in main.tsx). Nothing above the gate touches `document`, `window.dataLayer`, or
 * the network, so dev builds and sandbox deploys (which never set the var) are a
 * true no-op — Vite folds the condition to a constant and dead-code-eliminates
 * the rest. Covers both Capacitor shells too (they run this same bundle).
 *
 * Deliberate decision: `gtag('config', ..., { send_page_view: false })` disables
 * gtag.js's own automatic initial page_view. Every page_view — including the
 * first one — is sent by `sendPageView()` below, from one code path, so the
 * initial load can never be double-counted against the first router navigation.
 * A `lastKey` dedupe additionally guards against firing twice for the same
 * location regardless of whether TanStack fires `onResolved` for the initial
 * route as well.
 */
import type { AnyRouter } from '@tanstack/react-router';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

export function registerAnalytics(router: AnyRouter): void {
  if (!import.meta.env.PROD || !measurementId) return;

  window.dataLayer = window.dataLayer ?? [];
  // Classic gtag.js shape: push the real `arguments` object (not a rest-args
  // array) onto dataLayer — gtag.js's own processor expects that shape.
  function gtag(..._args: unknown[]) {
    window.dataLayer!.push(arguments);
  }
  window.gtag = gtag as Window['gtag'];

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(measurementId);
  document.head.appendChild(script);

  gtag('js', new Date());
  gtag('config', measurementId, { send_page_view: false });

  let lastKey: string | null = null;
  function sendPageView() {
    const key = router.state.location.href;
    if (key === lastKey) return;
    lastKey = key;
    window.gtag?.('event', 'page_view', {
      page_path: router.state.location.pathname,
      page_location: window.location.href,
      page_title: document.title,
    });
  }

  sendPageView();
  router.subscribe('onResolved', () => {
    sendPageView();
  });
}
